import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, ConfirmDialog, Input, Select, SkeletonRows, useToast } from "./ui";
import {
  gitRemoteService,
  type ConnectRemoteInput,
  type GitRemoteStatus,
} from "../services/git-remote.service";

/**
 * Git remoto opcional. Sem nada conectado o projeto continua versionado
 * localmente — o painel deixa isso explícito para o usuário.
 */
export default function GitRemotePanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [status, setStatus] = useState<GitRemoteStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [form, setForm] = useState<ConnectRemoteInput>({
    provider: "github",
    repository_url: "",
    default_branch: "main",
    username: "",
    access_token: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gitRemoteService.status(projectId);
      setStatus(data);
      if (data.remote) {
        setForm({
          provider: data.remote.provider,
          repository_url: data.remote.repository_url,
          default_branch: data.remote.default_branch,
          username: data.remote.username ?? "",
          access_token: "",
        });
      }
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(key: string, action: () => Promise<{ ok: boolean; message: string }>) {
    setBusy(key);
    try {
      const result = await action();
      toast[result.ok ? "success" : "error"](result.message || (result.ok ? "Concluído." : "Falhou."));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Operação falhou.");
    } finally {
      setBusy(null);
    }
  }

  async function connect() {
    setBusy("connect");
    try {
      await gitRemoteService.connect(projectId, {
        ...form,
        username: form.username || undefined,
        access_token: form.access_token || undefined,
      });
      toast.success("Remoto conectado.");
      setForm((current) => ({ ...current, access_token: "" }));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível conectar.");
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <Card title="Git remoto">
        <SkeletonRows rows={4} />
      </Card>
    );
  }

  const remote = status?.remote ?? null;
  const providers = status?.providers ?? [];
  const local = status?.local;

  return (
    <div className="space-y-4">
      <Card
        title="Git local"
        subtitle="Fonte primária de versionamento. Funciona sem GitHub, GitLab ou qualquer serviço externo."
      >
        <dl className="grid gap-3 text-xs sm:grid-cols-4">
          <div>
            <dt className="text-ink-muted">Repositório</dt>
            <dd className="mt-1">
              <Badge tone={local?.repository ? "ok" : "warn"}>
                {local?.repository ? "inicializado" : "ausente"}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-ink-muted">Branch</dt>
            <dd className="mt-1 text-ink">{local?.branch ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">HEAD</dt>
            <dd className="mt-1 font-mono text-ink">{local?.head?.slice(0, 10) ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-ink-muted">Alterações pendentes</dt>
            <dd className="mt-1 text-ink">{local?.pending_changes ?? 0}</dd>
          </div>
        </dl>
        {local?.branches?.length ? (
          <p className="mt-3 text-xs text-ink-muted">Branches: {local.branches.join(", ")}</p>
        ) : null}
      </Card>

      <Card
        title="Remoto conectado"
        subtitle="Opcional. Usado apenas para espelhar o histórico em um servidor externo."
        action={
          remote ? (
            <Badge tone={remote.last_status === "failed" ? "danger" : "ok"}>
              {remote.last_status ?? "conectado"}
            </Badge>
          ) : (
            <Badge tone="neutral">nenhum</Badge>
          )
        }
      >
        {remote ? (
          <div className="space-y-3">
            <dl className="grid gap-3 text-xs sm:grid-cols-3">
              <div>
                <dt className="text-ink-muted">Provedor</dt>
                <dd className="mt-1 text-ink">{remote.provider}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">Repositório</dt>
                <dd className="mt-1 break-all text-ink">{remote.repository_url}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">Branch padrão</dt>
                <dd className="mt-1 text-ink">{remote.default_branch}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">Token</dt>
                <dd className="mt-1 text-ink">{remote.has_token ? "armazenado (oculto)" : "não informado"}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">Última sincronização</dt>
                <dd className="mt-1 text-ink">
                  {remote.last_synced_at ? new Date(remote.last_synced_at).toLocaleString("pt-BR") : "—"}
                </dd>
              </div>
            </dl>

            {remote.last_message ? (
              <pre className="max-h-40 overflow-auto rounded-lg border border-line/60 bg-surface p-3 text-[11px] text-ink-muted">
                {remote.last_message}
              </pre>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                loading={busy === "test"}
                onClick={() => act("test", () => gitRemoteService.test(projectId))}
              >
                Testar conexão
              </Button>
              <Button
                size="sm"
                loading={busy === "push"}
                onClick={() => act("push", () => gitRemoteService.push(projectId))}
              >
                Push
              </Button>
              <Button
                size="sm"
                variant="secondary"
                loading={busy === "pull"}
                onClick={() => act("pull", () => gitRemoteService.pull(projectId))}
              >
                Pull (fast-forward)
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmDisconnect(true)}>
                Desconectar
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-ink-muted">
            Nenhum remoto configurado. O projeto continua versionado pelo Git local.
          </p>
        )}
      </Card>

      <Card title={remote ? "Atualizar conexão" : "Conectar repositório"}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Provedor"
            value={form.provider}
            options={providers.map((provider) => ({ value: provider.key, label: provider.label }))}
            onChange={(event) => setForm({ ...form, provider: event.target.value })}
          />
          <Input
            label="URL HTTPS do repositório"
            placeholder="https://github.com/usuario/repo.git"
            value={form.repository_url}
            onChange={(event) => setForm({ ...form, repository_url: event.target.value })}
          />
          <Input
            label="Branch padrão"
            value={form.default_branch ?? ""}
            onChange={(event) => setForm({ ...form, default_branch: event.target.value })}
          />
          <Input
            label="Usuário (opcional)"
            value={form.username ?? ""}
            onChange={(event) => setForm({ ...form, username: event.target.value })}
          />
          <Input
            label="Token de acesso"
            type="password"
            hint="Guardado criptografado no backend e nunca devolvido ao navegador."
            placeholder={remote?.has_token ? "•••••• (manter atual)" : ""}
            value={form.access_token ?? ""}
            onChange={(event) => setForm({ ...form, access_token: event.target.value })}
          />
        </div>
        <div className="mt-4">
          <Button size="sm" loading={busy === "connect"} onClick={connect} disabled={!form.repository_url}>
            {remote ? "Salvar" : "Conectar"}
          </Button>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmDisconnect}
        title="Desconectar remoto"
        description="O histórico local permanece intacto. Apenas a conexão externa é removida."
        confirmLabel="Desconectar"
        onCancel={() => setConfirmDisconnect(false)}
        onConfirm={async () => {
          setConfirmDisconnect(false);
          setBusy("disconnect");
          try {
            await gitRemoteService.disconnect(projectId);
            toast.success("Remoto desconectado.");
            await load();
          } catch (error) {
            toast.error(error instanceof Error ? error.message : "Falha ao desconectar.");
          } finally {
            setBusy(null);
          }
        }}
      />
    </div>
  );
}
